/**
 * FeedToasts — THE FEED WAVE's responsive layer, rendered from the ONE
 * channel store (§4). LIVE ws frames that are inbox-worthy and NOT for the
 * active conversation ACCUMULATE as store toasts (the channel fold —
 * useChannel → feedStore.foldLiveAnnouncement; a NEW NOTIFY entry arriving
 * on the inbox-row refresh toasts WITH ITS MESSAGE — the confirm-loop wave;
 * history hydration never reaches the fold, so it never toasts). This
 * component only renders the store slice: BOTTOM-LEFT stack (Ori's
 * placement, max 4), auto-dismiss ~6s, click = open the conversation via
 * the ONE existing open path (a toast without a conversation opens the
 * inbox overlay). No polling, no private frame bookkeeping.
 */
import React, { useEffect } from "react";

import { useConversationWorkspace } from "./ConversationWorkspace";
import { feedStore, useFeedStoreState, type FeedToast } from "./feedStore";
import type { ChannelApi } from "./useChannel";

const TOAST_MS = 6_000;

export const FeedToasts: React.FC<{ ch: ChannelApi }> = ({ ch }) => {
  const workspace = useConversationWorkspace();
  const { toasts } = useFeedStoreState();

  useEffect(() => {
    if (!toasts.length) return;
    const timer = window.setTimeout(() => feedStore.dismissOldestToast(), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [toasts]);

  const openToast = (toast: FeedToast) => {
    feedStore.dismissToast(toast.id);
    if (!toast.conversationId) {
      // a notify toast without a conversation opens the LIVE inbox overlay
      // (the confirm-loop wave — the inbox is the notification's home).
      feedStore.applyFeedRail("inbox", true);
      return;
    }
    workspace.setActiveConversationId(toast.conversationId, toast.label);
    workspace.setWorkspaceError("");
    void ch.ensureConversation(toast.conversationId).catch((error) => {
      workspace.setWorkspaceError(error instanceof Error ? error.message : String(error));
    });
  };

  if (!toasts.length) return null;
  return (
    <div
      data-feed-toasts
      aria-live="polite"
      style={{
        // BOTTOM-LEFT (Ori's placement, the confirm-loop wave).
        position: "fixed", left: 14, bottom: 14, zIndex: 60,
        display: "grid", gap: 8, justifyItems: "start",
      }}
    >
      {toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          data-feed-toast
          onClick={() => openToast(toast)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            maxWidth: "min(300px, 80vw)", cursor: "pointer",
            border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 10,
            background: "var(--color-bg, #fff)", color: "var(--color-text-primary, #111827)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.16)", padding: "9px 12px",
            fontSize: 12, fontWeight: 600, textAlign: "left",
          }}
          title={toast.label}
        >
          <span style={{ width: 7, height: 7, borderRadius: 4, background: "var(--accent-primary, #6B7EEA)", flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{toast.label}</span>
        </button>
      ))}
    </div>
  );
};
